import {CM_LOGGER_MATERIALS} from "@app/common/services/logging"
import {
    Component,
    computed,
    inject,
    input,
    viewChild,
    viewChildren,
    signal,
    afterNextRender,
    ElementRef,
    Injector,
    ChangeDetectionStrategy,
} from "@angular/core"
import {isUiItemVisible, MaterialNode, MaterialNodeSockets, MaterialUiInfo, SettingValueType} from "@cm/material-nodes"
import {SocketDirection, SocketPosition} from "@socket-editor/types"
import {MaterialInputSocketComponent} from "@material-editor/components/sockets/material-input-socket/material-input-socket.component"
import {MaterialOutputSocketComponent} from "@material-editor/components/sockets/material-output-socket/material-output-socket.component"
import {MaterialSettingComponent} from "@material-editor/components/sockets/material-setting/material-setting.component"
import {MatSelectModule} from "@angular/material/select"
import {NodeBaseComponent} from "@app/socket-editor/components/node-base/node-base.component"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {merge} from "rxjs"
import {SocketConnection} from "@app/socket-editor/services/socket-connections.service"
import {IsDefined} from "@cm/utils"
import {Point} from "@app/node-editor/types/navigation"
import {MaterialCustomItemComponent} from "@material-editor/components/nodes/material-custom-item/material-custom-item.component"
import {TippyDirective} from "@ngneat/helipopper"
import {ToggleComponent} from "@common/components/buttons/toggle/toggle.component"
import {MaterialSupportTooltipComponent} from "@app/material-editor/components/misc/tooltips/material-support-tooltip/material-support-tooltip.component"
import {PreviewSceneService} from "@material-editor/services/preview-scene.service"
import {DebugMaterialParametersTooltipComponent} from "@app/material-editor/components/misc/tooltips/debug-material-parameters-tooltip/debug-material-parameters-tooltip.component"
import {LocalStorageService} from "@app/common/services/local-storage/local-storage.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-material-node",
    imports: [
        MaterialInputSocketComponent,
        MaterialOutputSocketComponent,
        MaterialSettingComponent,
        MatSelectModule,
        MaterialCustomItemComponent,
        TippyDirective,
        ToggleComponent,
    ],
    templateUrl: "./material-node.component.html",
    styleUrl: "./material-node.component.scss",
})
export class MaterialNodeComponent extends NodeBaseComponent<MaterialNode> {
    private readonly logger = inject(CM_LOGGER_MATERIALS)
    private readonly injector = inject(Injector)
    public readonly $material = input.required<MaterialNode>({alias: "material"})
    public readonly $materialId = input.required<string>({alias: "materialId"})
    public readonly $materialRevisionId = input<string | undefined>(undefined, {alias: "materialRevisionId"})

    public readonly $info = computed<MaterialUiInfo<MaterialNodeSockets>>(() => this.$material().uiInfo())

    // read on every render rather than memoised, because it follows the settings the node is edited with
    isVisible(item: MaterialUiInfo<MaterialNodeSockets>["items"][number]): boolean {
        const material = this.$material()
        if (isUiItemVisible(item.definition, material.parameters.settings, material.parameters.inputs)) return true
        // an output socket holds no connection of its own, the node it feeds does, so ask the editor instead
        const definition = item.definition
        if (definition.custom || !definition.output) return false
        const nodeId = this.$nodeId()
        return this.connections.forNode(nodeId).some((connection) => connection.source?.nodeId === nodeId && connection.source?.socketId === definition.name)
    }

    readonly $nodeBox = viewChild.required<ElementRef<HTMLElement>>("nodeBox")
    readonly $inputSockets = viewChildren(MaterialInputSocketComponent)
    readonly $outputSockets = viewChildren(MaterialOutputSocketComponent)
    readonly previewSceneService = inject(PreviewSceneService)
    readonly localStorageService = inject(LocalStorageService)

    readonly $disabled = signal(false)
    toggleDisabled = () => {
        this.$material().disabled = !this.$material().disabled
        this.previewSceneService.$refresh.next()
    }

    // A widget grows when the image it shows arrives, which moves every socket below it. The connections are
    // drawn to measured positions, so the node has to measure again whenever it changes height. The host cannot
    // report that: it is positioned absolutely and so is the box inside it, which leaves the host out of its flow.
    override ngAfterViewInit() {
        super.ngAfterViewInit()
        let pending = false
        const observer = new ResizeObserver(() => {
            if (pending) return
            pending = true
            requestAnimationFrame(() => {
                pending = false
                void this.connections.refreshSocketPositions(this.$nodeId())
            })
        })
        observer.observe(this.$nodeBox().nativeElement)
        this.destroyRef.onDestroy(() => observer.disconnect())
    }

    override ngOnInit() {
        super.ngOnInit()

        this.node = this.$material()
        this.$disabled.set(this.$material().disabled)
        this.position = this.$material().position

        this.nodes.onUpdate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((nodes) => {
            if (nodes.some((node) => node.id === this.node.id)) {
                const newPosition = this.$info().editor.position
                if (newPosition.x !== this.position.x || newPosition.y !== this.position.y) {
                    this.position = newPosition
                }
            }
        })

        // a socket the mode hides loses its own component along with its row, so the node has to re-measure for it
        merge(this.connections.onAdd$, this.connections.onUpdate$, this.connections.onDelete$)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const visible = this.visibleSocketIds()
                if (visible === this.lastVisibleSocketIds) return
                this.lastVisibleSocketIds = visible
                afterNextRender(() => void this.connections.refreshSocketPositions(this.$nodeId()), {injector: this.injector})
            })
    }

    private lastVisibleSocketIds = ""

    private visibleSocketIds(): string {
        return this.$info()
            .items.filter((item) => this.isVisible(item))
            .map((item) => item.uniqueId)
            .join()
    }

    getSocketValueType(socketId: string, direction: SocketDirection): SettingValueType | undefined {
        if (direction === "input") {
            return this.$inputSockets()
                .find((nodeSocket) => nodeSocket.$socket().id === socketId)
                ?.$socket().valueType
        } else {
            return this.$outputSockets()
                .find((nodeSocket) => nodeSocket.$socket().id === socketId)
                ?.$socket().valueType
        }
    }

    getSocketPosition(socketId: string, direction: SocketDirection): SocketPosition | undefined {
        if (direction === "input") {
            const nodeSockets = this.$inputSockets()
            const nodeSocket = nodeSockets.find((nodeSocket) => nodeSocket.$socket().id === socketId)
            if (!nodeSocket) {
                this.logger.error(
                    `Failed to find input socket with id ${socketId} on ${this.$material()?.nodeClass}, available: [${nodeSockets.map((socket) => socket.$socket().id).join(", ")}]`,
                )
            }
            return nodeSocket?.getSocketPosition()
        } else {
            const nodeSockets = this.$outputSockets()
            const nodeSocket = nodeSockets.find((nodeSocket) => {
                return nodeSocket.$socket().id === socketId
            })
            if (!nodeSocket) {
                this.logger.error(
                    `Failed to find output socket with id ${socketId} on ${this.$material()?.nodeClass}, available: [${nodeSockets.map((socket) => socket.$socket().id).join(", ")}]`,
                )
            }
            return nodeSocket?.getSocketPosition()
        }
    }

    readonly $iconClass = computed(() => {
        if (this.$info()?.support.cycles && this.$info()?.support.three) {
            return "fa-info-circle"
        }
        return "fa-warning fa-solid cm-text-warning"
    })

    readonly $nodeId = computed(() => this.$info().id)

    override set position({x, y}: {x: number; y: number}) {
        const previousPosition = {...this.position}
        super.position = {x, y}
        // Only write to model if effective position changed (avoids marking dirty during init
        // when setting position to the default value that the getter already returns)
        const effectivePosition = this.$material().position
        if (effectivePosition.x !== x || effectivePosition.y !== y) {
            this.$material().position = {x, y}
        }
        if (previousPosition) {
            this.connections.update(this.offsetSocketPositions({x: x - previousPosition.x, y: y - previousPosition.y}))
        }
    }
    override get position() {
        return super.position
    }

    offsetSocketPositions({x, y}: Point, connections: SocketConnection[] = this.connections.forNode(this.$nodeId())) {
        return connections
            .map((connection) => {
                if (connection.destination.nodeId === this.$nodeId() && connection.destination.socketId) {
                    const newPosition = {x: connection.destination.position.x + x, y: connection.destination.position.y + y}
                    if (newPosition) {
                        return {
                            ...connection,
                            destination: {...connection.destination, position: newPosition},
                        }
                    }
                }
                if (connection.source.nodeId === this.$nodeId() && connection.source.socketId) {
                    const newPosition = {x: connection.source.position.x + x, y: connection.source.position.y + y}
                    if (newPosition) {
                        return {
                            ...connection,
                            source: {...connection.source, position: newPosition},
                        }
                    }
                }
                return connection
            })
            .filter(IsDefined)
    }

    public MaterialSupportTooltipComponent = MaterialSupportTooltipComponent
    public DebugMaterialParametersTooltipComponent = DebugMaterialParametersTooltipComponent
}
